import React, { useCallback, useEffect, useMemo } from 'react';

import { CalloutV2, Intent } from '@varicent/components';
import { Form, Formik } from 'formik';

import { BasicSelectField } from 'components/BasicSelect/BasicSelectField';
import TextButton from 'components/Buttons/TextButton/TextButton';
import Dialog from 'components/Dialog/Dialog';

import { AttributeFilterBuilder } from 'app/components/AccountAttributeFilter/AttributeFilterBuilder';
import {
  ClauseValue,
  filterModelToFormState,
  formStateToFilterModel,
  validateAttributeFilterField
} from 'app/components/AccountAttributeFilter/AttributeFilterUtils';
import { useAccountAttributes } from 'app/components/AccountAttributeFilter/useAccountAttributes';

import { useMapContextRedistributor } from 'app/contexts/MapContextRedistributor/mapContextRedistributorProvider';

import { AccountSourceFilterEnum } from 'app/graphql/generated/graphqlApolloTypes';
import { useUpsertBattleCard } from 'app/graphql/mutations/upsertBattlecard';
import { useGetBattlecardAccountFilters } from 'app/graphql/queries/getBattlecardAccountFilters';
import { GET_MAP_VISUALIZATION_SETTINGS_NAME } from 'app/graphql/queries/getMapVisualizationSettings';

import useCanUser from 'app/hooks/useCanUser';
import useShowToast from 'app/hooks/useShowToast';

import block from 'utils/bem-css-modules';
import { formatMessage } from 'utils/messages/utils';
import { UserAction } from 'utils/permissions/userActions';

import style from './AccountAttributeFilterDialog.module.pcss';

const b = block(style);

export type AccountAttributeFilterDialogProps = {
  onUpdate: () => void;
  onClose: () => void;
};

type AccountAttributeFilterForm = {
  sourceFilter: AccountSourceFilterEnum;
  attributeFilter: Array<ClauseValue>;
};

export const AccountAttributeFilterDialog = ({ onUpdate, onClose }: AccountAttributeFilterDialogProps) => {
  const accountAttributes = useAccountAttributes();
  const { selectedDeploymentModelId, selectedBattleCardId } = useMapContextRedistributor();

  const showToast = useShowToast();
  const [upsertBattlecard] = useUpsertBattleCard();
  const handleFormikSubmit = async (values: AccountAttributeFilterForm) => {
    const accountAttributeFilter = JSON.stringify(
      values.sourceFilter === AccountSourceFilterEnum.CustomerAccountHierarchy
        ? formStateToFilterModel(accountAttributes.attributes, values.attributeFilter)
        : {}
    );
    await upsertBattlecard({
      variables: {
        input: {
          deploymentModelId: selectedDeploymentModelId,
          battlecardId: +selectedBattleCardId,
          accountAttributeFilter,
          accountSourceFilter: values.sourceFilter
        }
      },
      refetchQueries: [GET_MAP_VISUALIZATION_SETTINGS_NAME],
      awaitRefetchQueries: true,
      onError() {
        showToast(formatMessage('UPDATE_BATTLECARD_ERROR'), Intent.DANGER);
      }
    });
    onUpdate();
    onClose();
  };

  const bcFilters = useGetBattlecardAccountFilters({
    fetchPolicy: 'network-only',
    variables: {
      deploymentModelId: selectedDeploymentModelId,
      battlecardId: +selectedBattleCardId
    }
  });
  const bcData = bcFilters.data?.getDeploymentModelSpec?.battlecards[0];

  const initialValues = useMemo(
    (): AccountAttributeFilterForm => ({
      sourceFilter: bcData?.accountSourceFilter,
      attributeFilter: filterModelToFormState(
        bcData?.accountAttributeFilter ? JSON.parse(bcData.accountAttributeFilter) : null
      )
    }),
    [bcData]
  );

  const validate = useCallback(
    (values: AccountAttributeFilterForm) => {
      const attrFilterErrors = validateAttributeFilterField(accountAttributes.attributes, values.attributeFilter);
      if (attrFilterErrors) return { attributeFilter: attrFilterErrors };
      return undefined;
    },
    [accountAttributes.attributes]
  );

  useCreatePortalElement({
    id: portalId,
    className: b('portal')
  });

  const hasError = !!(accountAttributes.error || bcFilters.error);
  const isReadOnly = !useCanUser(UserAction.BATTLE_CARD_CANVAS_MODIFY);

  return (
    <Formik<AccountAttributeFilterForm>
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleFormikSubmit}
      validate={validate}
    >
      {({ submitCount, values, isSubmitting, setFieldValue, handleSubmit }) => {
        const showFilterBuilder = values.sourceFilter === AccountSourceFilterEnum.CustomerAccountHierarchy;
        return (
          <Dialog
            isLoading={accountAttributes.loading || bcFilters.loading}
            title={formatMessage('ACCOUNT_SEGMENTATION')}
            isOpen
            onClose={onClose}
            onSubmit={handleSubmit}
            confirmButtonText={formatMessage('UPDATE')}
            confirmButtonLoading={isSubmitting}
            disableConfirm={isReadOnly}
            showConfirm={!hasError}
          >
            {hasError ? (
              <ErrorDialogCallout />
            ) : (
              <Form>
                <p>{formatMessage('ACCOUNT_ATTRIBUTE_FILTER_DIALOG_DESCRIPTION')}</p>

                <div className={b('topRow')}>
                  <label className={b('sourceFilterLabel')}>
                    {formatMessage('SHOW_ACCOUNTS_BASED_ON')}
                    <BasicSelectField<AccountSourceFilterEnum>
                      name="sourceFilter"
                      items={ACCOUNT_SOURCE_FILTER_ITEMS}
                      label={formatMessage('SHOW_ACCOUNTS_BASED_ON')}
                      disabled={isReadOnly}
                      placement="bottom"
                    />
                  </label>

                  {showFilterBuilder && values.attributeFilter.length > 0 && (
                    <TextButton
                      text={formatMessage('CLEAR_ALL')}
                      testId="clear-all-attributes"
                      onClick={() => setFieldValue('attributeFilter', [])}
                      type="button"
                      large={false}
                      minimal
                      disabled={isReadOnly}
                    />
                  )}
                </div>

                {showFilterBuilder ? (
                  <>
                    <AttributeFilterBuilder
                      name="attributeFilter"
                      attributes={accountAttributes.attributes}
                      showErrors={submitCount > 0}
                      portalId={portalId}
                      disabled={isReadOnly}
                    />
                    <AccountAttributeFilterDialogCallout />
                  </>
                ) : (
                  <SalesActivityCallout />
                )}
              </Form>
            )}
          </Dialog>
        );
      }}
    </Formik>
  );
};

const SalesActivityCallout = () => (
  <CalloutV2 intent={Intent.WARNING}>
    <p data-testid="account-attribute-filter-dialog-sales-activity-callout">
      {formatMessage('ACCOUNT_ATTRIBUTE_FILTER_DIALOG_SALES_ACTIVITY_CALLOUT')}
    </p>
  </CalloutV2>
);

const AccountAttributeFilterDialogCallout = () => (
  <CalloutV2 intent={Intent.WARNING}>
    <p data-testid="account-attribute-filter-dialog-warning-callout">
      {formatMessage('ACCOUNT_ATTRIBUTE_FILTER_DIALOG_WARNING_CALLOUT')}
    </p>
  </CalloutV2>
);

const ErrorDialogCallout = () => (
  <CalloutV2 intent={Intent.DANGER}>
    <p data-testid="account-attribute-filter-dialog-error-callout">
      {formatMessage('ACCOUNT_ATTRIBUTE_FILTER_DIALOG_ERROR_CALLOUT')}
    </p>
  </CalloutV2>
);

export const ACCOUNT_SOURCE_FILTER_ITEMS = [
  {
    value: AccountSourceFilterEnum.CustomerAccountHierarchy,
    text: formatMessage('ACCOUNT_HIERARCHY')
  },
  {
    value: AccountSourceFilterEnum.Activity,
    text: formatMessage('SALES_ACTIVITY')
  }
];

const useCreatePortalElement = (attributes: { id: string; className: string }) => {
  useEffect(() => {
    const element = document.createElement('div');
    Object.assign(element, attributes);
    document.body.appendChild(element);
    return () => {
      element.remove();
    };
  }, []);
};

const portalId = `account-attribute-filter-dialog-portal`;
